import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Bot, 
  Send, 
  MessageCircle, 
  RotateCcw, 
  ArrowLeft,
  ChevronRight,
  ShieldCheck,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { generateContentWithCache } from '../../lib/geminiService';
import { parseMarkdownToReact } from '../../utils/markdownUtils';

interface HelpMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface HelpBotChatProps {
  onBack: () => void;
  userNiche?: string;
  userPlan?: string;
}

const RMAX_KNOWLEDGE_BASE = `
VOCÊ É O ASSISTENTE OFICIAL DE DÚVIDAS E SUPORTE DO R-MAX.
Seu objetivo é ajudar o prestador de serviços ou autônomo a tirar qualquer dúvida sobre o uso do aplicativo R-MAX, lógica de precificação e planos de assinatura.

INFORMAÇÕES OFICIAIS DO SISTEMA R-MAX:

1. ESTRUTURA DOS 3 PLANOS OFICIAIS:
- PLANO GRATUITO (R$ 0):
  * 5 orçamentos profissionais por mês.
  * Cálculo automático do valor da sua hora de trabalho.
  * Catálogo com 10 modelos prontos de serviços por profissão.
  * Compartilhamento direto de proposta no WhatsApp e PDF.
  * Sem cobrança, sem fidelidade e sem cartão de crédito.

- PLANO PRO (⚡ R$ 19,90/mês ou R$ 195/ano em até 10x):
  * Orçamentos ILIMITADOS em PDF.
  * Centro de Custos Completo: Cadastro de Insumos/Materiais com margem de revenda, Fichas Técnicas (Receitas/Kits) e Catálogo de Serviços.
  * Histórico Completo de Orçamentos de todos os meses anteriores com busca e reuso.
  * Cálculo do valor da sua hora com divisão das contas fixas e seu salário desejado.
  * Preços da Região (Médias de mercado da sua cidade).
  * Controle de Vendas simples.

- PLANO ESPECIALISTA PREMIUM (👑 R$ 49,90/mês ou plano anual):
  * Tudo do Plano PRO, MAIS:
  * ✍️ Assinatura do Cliente no Celular: Coleta de assinatura do cliente direto na tela do celular no PDF.
  * 📸 Ordens de Serviço (OS) com Fotos: Registro de fotos (Antes, Durante e Depois da execução) para comprovação e segurança contra reclamações.
  * 🧾 Emissor de Recibos Profissionais com QR Code PIX: Geração de comprovante com chave PIX e Copia & Cola em segundos.
  * 📅 Agenda de Atendimentos e Horários: Grade para equipe ou atendimento próprio com controle de sessões e horários.
  * 🌐 Vitrine de Agendamento Online Pública: Link personalizado com página para clientes agendarem serviços.
  * 👥 Cadastro de Clientes com Mapa: Histórico de serviços e botão que abre o endereço no Google Maps/Waze.
  * 📊 Painel Financeiro de Resultados: Visão clara do lucro líquido real, contas a pagar e horas trabalhadas.

2. LÓGICA DE PRECIFICAÇÃO E VALOR DA HORA:
- O valor da hora é calculado pela fórmula: (Salário Desejado + Custos Fixos do Mês) / (Horas reais trabalhadas no mês).
- Custos Fixos incluem: Aluguel, MEI/DAS, energia, internet, manutenção de ferramentas, transporte, etc.
- Margem de Lucro Real: O R-MAX protege o lucro descontando impostos e taxas de maquininha antes de definir o preço final.

3. MÓDULOS DE GESTÃO DO DIA A DIA:
- Assinatura na Tela: Pode ser coletada diretamente na entrega do serviço na tela do celular.
- Recibo PIX: Preenche nome do cliente, valor e serviço, gerando PDF imediato com QR Code para quitação.
- Custos & Insumos: Ao atualizar o preço de um material, todas as receitas e orçamentos futuros são recalculados automaticamente.
- Ordem de Serviço (OS): Permite vincular veículos (placa, modelo) ou equipamentos, controlar peças, mão de obra e fotos do serviço.

4. REGRAS DE ATENDIMENTO E TOM DE VOZ:
- Seja extremamente acolhedor, profissional, claro e objetivo, usando linguagem simples para pessoas com 50 anos ou mais.
- Nunca use siglas sem explicação (evite termos como VHB, ERP, CRM, DRE, Cockpit, etc.).
- Use emojis para tornar a leitura agradável.
- Responda em tópicos curtos quando houver passos práticos.
- Se o usuário perguntar sobre problemas de pagamento ou erros técnicos que exijam intervenção humana, forneça a resposta preliminar e indique que ele pode chamar o suporte oficial no WhatsApp (11) 92089-7453.
`;

const SUGGESTED_QUESTIONS = [
  "Como funciona a assinatura na tela?",
  "Qual a diferença do PRO para o Especialista?",
  "Como o sistema calcula o valor da minha hora?",
  "Como emitir recibo com QR Code PIX?",
  "Como funciona a OS com fotos do serviço?",
  "Como ter o link de agendamento online?"
];

export default function HelpBotChat({ onBack, userNiche, userPlan }: HelpBotChatProps) {
  const [messages, setMessages] = useState<HelpMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: `Olá! Sou o **Assistente de Dúvidas do R-MAX** 🤖⚡\n\nComo posso te ajudar hoje? Você pode tirar dúvidas sobre como usar qualquer ferramenta, entender os cálculos de lucro e custos, ou conhecer os recursos dos planos **Grátis**, **PRO** e **Especialista**.`
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isTyping) return;

    const userMsg: HelpMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: text.trim()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    try {
      const promptContext = `${RMAX_KNOWLEDGE_BASE}
      
CONDIÇÃO ATUAL DO USUÁRIO:
- Plano Atual: ${userPlan || 'Não especificado'}
- Profissão / Nicho: ${userNiche || 'Prestador de Serviços Geral'}

Histórico da conversa:
${messages.slice(-4).map(m => `${m.role === 'user' ? 'Usuário' : 'Assistente'}: ${m.content}`).join('\n')}
Usuário: ${text}

Responda em formato markdown legível com parágrafos claros e amigáveis:`;

      const response = await generateContentWithCache({
        model: 'gemini-3.5-flash-lite',
        contents: [{ parts: [{ text: promptContext }] }],
        signal: controller.signal,
        skipCache: false
      });

      const reply = response.text || 'Desculpe, não consegui entender completamente. Poderia reformular sua dúvida?';

      setMessages(prev => [
        ...prev,
        {
          id: `bot_${Date.now()}`,
          role: 'assistant',
          content: reply
        }
      ]);
    } catch (err: any) {
      console.error('Erro no HelpBot:', err);
      setError('Tivemos uma pequena instabilidade na conexão. Tente perguntar novamente ou chame no WhatsApp.');
      setMessages(prev => [
        ...prev,
        {
          id: `bot_err_${Date.now()}`,
          role: 'assistant',
          content: 'Desculpe, tive uma instabilidade momentânea na conexão. Por favor, tente enviar novamente ou toque no botão abaixo para falar com nosso suporte humano no WhatsApp!'
        }
      ]);
    } finally {
      clearTimeout(timeoutId);
      setIsTyping(false);
    }
  }, [isTyping, messages, userPlan, userNiche]);

  const handleClearHistory = () => {
    setMessages([
      {
        id: `welcome_${Date.now()}`,
        role: 'assistant',
        content: `Histórico reiniciado! 🔄 Sobre qual funcionalidade ou cálculo do R-MAX você quer saber agora?`
      }
    ]);
    setError(null);
  };

  const whatsappSupportUrl = "https://wa.me/5511920897453?text=" + encodeURIComponent("Olá! Estou com uma dúvida no R-MAX e gostaria de suporte.");

  return (
    <div className="flex flex-col h-full min-h-120 max-h-[calc(100dvh-5rem)] bg-zinc-50">
      {/* Header */}
      <div className="bg-brand p-4 text-white flex items-center justify-between shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-1.5 bg-white/20 hover:bg-white/30 rounded-xl transition-colors cursor-pointer"
            title="Voltar ao Menu"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
              <Bot className="h-4 w-4 text-emerald-200" />
            </div>
            <div>
              <h3 className="font-bold text-xs flex items-center gap-1.5">
                <span>R-MAX Responde</span>
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              </h3>
              <p className="text-[10px] text-white/80">Tira-Dúvidas Inteligente 24h</p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleClearHistory}
          className="p-1.5 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-xl transition-colors cursor-pointer flex items-center gap-1 text-[10px]"
          title="Limpar conversa"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Reiniciar</span>
        </button>
      </div>

      {/* Messages Feed */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 text-xs">
        {messages.map((msg) => (
          <motion.div
            key={msg.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl p-3.5 shadow-xs leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-emerald-700 text-white rounded-br-none'
                  : 'bg-white text-zinc-800 border border-zinc-200/70 rounded-bl-none'
              }`}
            >
              {parseMarkdownToReact(msg.content)}
            </div>
          </motion.div>
        ))}

        {isTyping && (
          <div className="flex items-center gap-2 text-zinc-400 text-xs py-1 px-2">
            <Bot className="h-4 w-4 animate-bounce text-brand" />
            <span className="animate-pulse">R-MAX está digitando a resposta...</span>
          </div>
        )}

        {/* Sugestões de perguntas rápidas */}
        {messages.length === 1 && !isTyping && (
          <div className="pt-2 space-y-1.5">
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider px-1">
              Perguntas frequentes:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {SUGGESTED_QUESTIONS.map((q, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSendMessage(q)}
                  className="p-2.5 bg-white hover:bg-emerald-50/50 border border-zinc-200/80 hover:border-brand rounded-xl text-left text-zinc-700 hover:text-brand font-medium text-[11px] transition-all flex items-center justify-between cursor-pointer shadow-2xs group active:scale-98"
                >
                  <span className="truncate pr-1">{q}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-zinc-300 group-hover:text-brand shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Fallback WhatsApp Banner */}
      <div className="px-4 py-2 bg-zinc-100 border-t border-zinc-200 flex items-center justify-between text-[11px] text-zinc-600">
        <span className="truncate">Precisa de suporte humano?</span>
        <a
          href={whatsappSupportUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 shrink-0 ml-2"
        >
          <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
          <span>Chamar no WhatsApp</span>
        </a>
      </div>

      {/* Input Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage(inputValue);
        }}
        className="p-3 bg-white border-t border-zinc-200 flex items-center gap-2"
      >
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Digite sua dúvida sobre o R-MAX..."
          disabled={isTyping}
          className="flex-1 bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/30 font-medium"
        />
        <button
          type="submit"
          disabled={!inputValue.trim() || isTyping}
          className="p-2.5 bg-brand hover:bg-brand-dark disabled:opacity-40 text-white rounded-xl transition-all cursor-pointer shadow-xs active:scale-95 shrink-0"
          title="Enviar pergunta"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
